// Touchpoints.tsx — every contact with a resident, from every team, in one list.
//
// SHARED by the case record and by Resident inquiry. The claim this product makes is that a
// household has ONE history; two implementations of that list would quietly falsify the claim the
// first time one of them started filtering differently.
//
import { useState } from 'react'
import { api } from '../../lib/api'
import { dateOf, ROLE_LABELS } from '../../lib/format'

type Data = Awaited<ReturnType<typeof api.ams.case>>

/**
 * Every contact with this resident, from every team, in one list.
 *
 * This is the answer to the problem the whole module exists for. Today a call centre agent does
 * not know the Property Officer knocked last Tuesday; the Property Officer does not know the
 * resident rang in on Monday; the Community Liaison Team arrives to discuss hardship at a door
 * where a summons has already been recommended. Each team keeps its own record and nobody holds
 * the resident's.
 *
 * Three things are shown that the previous version dropped, and each is the reason someone opens
 * this screen:
 *
 *   THE NOTE. "Daughter says he was discharged from hospital in June" is the handover. A timeline
 *   of bare event types — visit, outreach, letter — records that something happened and loses the
 *   only part that changes what the next officer does.
 *
 *   WHO. A name, not a role. "Serene Tan (Community Liaison Team)" is answerable; "clt_officer" is
 *   not, and a resident asking "who told me that?" is asking for the name. Automated outreach has
 *   no name and says so, rather than borrowing one.
 *
 *   THE SOURCE, as a filter. An officer preparing for a doorstep wants the last three human
 *   conversations, not forty automated nudges — but the nudges are not hidden by default, because
 *   a history that silently omits what was sent to a resident is worse than a long one.
 */
interface Source { key: string; label: string; kinds: string[] }
const ALL_SOURCES: Source = { key: 'all', label: 'Everything', kinds: [] }
const SOURCES: Source[] = [
  ALL_SOURCES,
  { key: 'people', label: 'People', kinds: ['contact', 'visit'] },
  { key: 'contact', label: 'Calls and counter', kinds: ['contact'] },
  { key: 'visit', label: 'Visits', kinds: ['visit'] },
  { key: 'legal', label: 'Letters and legal', kinds: ['legal', 'letter'] },
  { key: 'outreach', label: 'Automated', kinds: ['outreach'] },
  { key: 'payment', label: 'Payments', kinds: ['payment'] },
]

export default function Touchpoints({ events }: { events: Data['events'] }) {
  const [src, setSrc] = useState('all')
  const filter = SOURCES.find(s => s.key === src) ?? ALL_SOURCES
  const shown = filter.kinds.length === 0
    ? events : events.filter(e => filter.kinds.includes(e.kind))

  // Counts come from the data, so a source with nothing on this case reads as "0" rather than
  // offering a filter that silently returns an empty list.
  const countOf = (f: Source) =>
    f.kinds.length === 0 ? events.length : events.filter(e => f.kinds.includes(e.kind)).length

  return (
    <section className="card">
      <h3>Every touchpoint</h3>
      <p className="muted sm">
        Every call, counter visit, house visit, letter, automated message and payment for this
        resident — from all four teams, in one place.
      </p>

      <div className="chips tp-filters">
        {SOURCES.map(f => (
          <button key={f.key} className={`chip ${src === f.key ? 'on' : ''}`}
                  onClick={() => setSrc(f.key)}>
            {f.label} <span className="xs">{countOf(f)}</span>
          </button>
        ))}
      </div>

      <ul className="timeline tp">
        {shown.map(e => {
          const detail = (e.detail ?? {}) as Record<string, unknown>
          const notes = typeof detail.notes === 'string' ? detail.notes : null
          const spokeTo = typeof detail.spokeTo === 'string' ? detail.spokeTo : null
          return (
            <li key={e.id} className={`tp-${e.kind}`}>
              <div className="tp-top">
                <b>{e.summary}</b>
                <span className="muted xs">{dateOf(e.at)}</span>
              </div>
              <div className="muted xs tp-who">
                {/* An unattributed event is automated. It says so instead of being left blank,
                    which would read as missing data rather than as "no human did this". */}
                {e.actorName
                  ? <>{e.actorName}{e.actorRole
                      ? ` · ${ROLE_LABELS[e.actorRole] ?? e.actorRole}` : ''}</>
                  // No person behind it. The role key IS the actor here — 'ai_outreach' and
                  // 'system' both label themselves — so printing a generic "Automated" as well
                  // gave "Automated · Automated outreach".
                  : <>{e.actorRole ? ROLE_LABELS[e.actorRole] ?? e.actorRole : 'Automated'}</>}
                {e.channel ? ` · ${e.channel.replace(/_/g, ' ')}` : ''}
                {spokeTo ? ` · spoke to ${spokeTo}` : ''}
              </div>
              {notes && <p className="tp-note">{notes}</p>}
            </li>
          )
        })}
        {shown.length === 0 && (
          <li className="muted">
            Nothing recorded from this source. That is a real result, not a failed load.
          </li>
        )}
      </ul>
    </section>
  )
}
